import React from 'react';
import { Link } from 'react-router-dom';
import {
    DocPage,
    Note,
    Steps,
    Crumbs,
    WqTable,
    API_KEYS_PATH,
    KEY_MODES,
    MERCHANT_ID_LOCATION,
} from '../../components/documentation/web-integration';
import TE1 from '../../assets/docs/test-env-dark.png';

const CONTENTS = ['Test mode', 'Working in test mode', 'Going live'];

const TEST_STEPS = [
    <>
        <strong>Log in to your dashboard.</strong> New accounts open in Test mode — the{' '}
        <strong>Test / Live</strong> toggle at the top of the dashboard shows <strong>Test</strong>.
    </>,
    <>
        <strong>Copy your Merchant ID</strong>, shown {MERCHANT_ID_LOCATION}.
    </>,
    <>
        <strong>Generate a test secret key</strong> under Settings › WayaQuick Settings › API Keys. It starts with{' '}
        <code>WAYASECK_TEST_</code>.{' '}
        <Link to="/api-encryption" className="wq-link">
            Step-by-step guide
        </Link>
    </>,
    <>
        <strong>Add the key and your Merchant ID to your integration</strong> — a plugin, an SDK or the Standard API.
    </>,
    <>
        <strong>Make test payments</strong> with the{' '}
        <Link to="/test-card-information" className="wq-link">
            test cards
        </Link>{' '}
        and check the results in your dashboard.
    </>,
];

const LIVE_STEPS = [
    <>
        <strong>Complete your KYC</strong> from your dashboard: your personal details, business details, directors
        and documents.
    </>,
    <>
        <strong>Wait for approval.</strong> Your dashboard shows the review status and updates by itself once it's
        approved or rejected. If it's rejected, you'll see the reason and can fix and resubmit.
    </>,
    <>
        <strong>Switch the toggle to Live.</strong> It stays locked until your KYC is approved.
    </>,
    <>
        <strong>Generate a live secret key</strong> (it starts with <code>WAYASECK_PROD_</code>) and replace your test
        key with it. Nothing else in your code needs to change.
    </>,
];

function TestEnv() {
    return (
        <DocPage
            seo={{
                title: 'Test Environment',
                description:
                    'Build and try your WayaQuick integration in test mode with simulated payments, then go live once your KYC is approved.',
            }}
            eyebrow="Developers · Test mode"
            title={
                <>
                    WayaQuick — <em>Test Environment</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Test Environment · Test payments never move real money."
            prev={{ to: '/dev-get-started', label: 'Getting Started' }}
            next={{ to: '/api-encryption', label: 'API Key Guide' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Test mode</h2>
                <p className="wq-lead">
                    Test mode lets you build and try your integration with simulated payments that work just like real
                    ones. Every new WayaQuick account starts in Test mode, so you can start building straight away —
                    no approval needed.
                </p>
                <WqTable columns={KEY_MODES.columns} rows={KEY_MODES.rows} />
                <Note label="Kept separate">
                    Test transactions, balances and keys are kept apart from live ones. A test key can never take a
                    real payment, and nothing you do in Test mode affects your live account.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Working in test mode</h2>
                <Steps items={TEST_STEPS} />

                <h4 className="wq-h4">Where to find your test key</h4>
                <Crumbs items={API_KEYS_PATH} />

                <figure className="my-6">
                    {/* Replace test-env1.png with a current screenshot of the dashboard. */}
                    <img src={TE1} alt="WayaQuick dashboard in Test mode" className="w-full rounded-lg" />
                </figure>

                <Note label="Test mode only">
                    Use test keys only while building. Before your site or app goes public, switch to your live key —
                    otherwise customers' payments won't be real.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Going live</h2>
                <p className="wq-p">
                    Live mode is available once your KYC has been approved. Until then, the toggle stays on Test.
                </p>
                <Steps items={LIVE_STEPS} />
                <p className="wq-p">
                    Once you're live, you can switch between Test and Live at any time — for example, to try a new
                    feature in Test mode before using it with real customers. Your dashboard shows the data for
                    whichever mode is selected.
                </p>
            </section>
        </DocPage>
    );
}

export default TestEnv;